import React from "react";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PALETTES } from "@/lib/chartTheme";
import {
  Palette, Image as ImageIcon, Frame, Tags, ListOrdered, Ruler,
  MessageSquare, Shapes, Hash, Paintbrush,
} from "lucide-react";
import { CondFormatEditor } from "@/components/bi/CondFormatEditor";
import { AppearanceEditor, LegendEditor, TooltipEditor, MeasureFormatEditor, EmptyAsSelect } from "@/components/bi/StyleEditors";
import {
  AxisEditor, AxisFmtEditor, DataLabelEditor, ComboLabelEditor, BarStyleEditor, LineStyleEditor, SeriesStyleEditor, PieStyleEditor, FunnelStyleEditor,
  ScatterStyleEditor, RadarStyleEditor, GaugeStyleEditor, TreemapStyleEditor, HeatmapStyleEditor,
  SeriesColorsEditor, MapStyleEditor, PointMapStyleEditor, GoogleMapStyleEditor, ComboSeriesEditor, TornadoStyleEditor,
} from "@/components/bi/ChartStyleEditors";
import { SortEditor, TooltipStyleEditor, RangeColorsEditor, AdvancedColorEditor, ImageWidgetEditor, AnalyticsEditor, ADV_COLOR_TYPES } from "@/components/bi/FormatEditors";
import { CARTESIAN_TYPES, BAR_TYPES } from "@/lib/chartStyle";
import { MediaInput } from "@/components/bi/MediaInput";
import { KpiEditor } from "@/components/bi/KpiEditor";
import { TableEditor } from "@/components/bi/TableEditor";
import { PivotEditor } from "@/components/bi/PivotEditor";
import { Section, StyleToggle } from "@/components/bi/InspectorBits";

// Sections 4-12 of the standardized 14-section properties structure (item 188).

// Section 4 — Background & Border
export function InspectorBackgroundSection({ ins }) {
  const { opts, patchOpts } = ins;
  return (
    <Section value="background" icon={Frame} title="Background & Border" testid="inspector-sec-background"
      hint="Card background color, border, corner radius and shadow.">
      <AppearanceEditor opts={opts} patchOpts={patchOpts} />
    </Section>
  );
}

// Section 5 — Colors & Palette
export function InspectorColorsSection({ ins }) {
  const { widget, opts, patchOpts, isGeo } = ins;
  return (
    <Section value="colors" icon={Palette} title="Colors" testid="inspector-sec-colors"
      hint="Palette, gradients and per-series color overrides.">
      <div>
        <Label className="text-xs text-[color:var(--ink-3)] flex items-center gap-1"><Palette className="w-3.5 h-3.5" />Color palette</Label>
        <Select value={opts.palette || "Eazy Core"} onValueChange={(v) => patchOpts({ palette: v })}>
          <SelectTrigger data-testid="style-palette" className="mt-1.5 w-full bg-[color:var(--surface)]"><SelectValue /></SelectTrigger>
          <SelectContent>{Object.keys(PALETTES).map((p) => (
            <SelectItem key={p} value={p}>
              <div className="flex items-center gap-2">
                <span className="flex">{PALETTES[p].slice(0, 5).map((c, i) => <span key={`${c}-${i}`} style={{ background: c }} className="w-2.5 h-2.5 rounded-full -ml-0.5 border border-white" />)}</span>
                {p}
              </div>
            </SelectItem>
          ))}</SelectContent>
        </Select>
      </div>
      {!isGeo && (
        <StyleToggle label="Color by category" testid="style-colorbycat" checked={opts.colorByCategory !== false} onChange={(v) => patchOpts({ colorByCategory: v })} />
      )}
      <StyleToggle label="Gradient fills" testid="style-gradient" checked={opts.gradient !== false} onChange={(v) => patchOpts({ gradient: v })} />
      {["pie", "donut"].includes(widget.type) && (
        <>
          <StyleToggle label="Gradient slices" testid="style-pie-gradient" checked={!!opts.pieGradient} onChange={(v) => patchOpts({ pieGradient: v })} />
          {opts.pieGradient && (
            <div className="flex items-center justify-between gap-2 py-1">
              <Label className="text-sm text-[color:var(--ink-2)]">Gradient type</Label>
              <Select value={opts.pieGradientType || "radial"} onValueChange={(v) => patchOpts({ pieGradientType: v })}>
                <SelectTrigger data-testid="style-pie-gradient-type" className="bg-[color:var(--surface)] w-[100px] h-8 text-xs shrink-0"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="radial">Radial</SelectItem>
                  <SelectItem value="linear">Linear</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}
        </>
      )}
      {["bar", "bar_stacked", "line", "area", "combo", "pie", "donut", "funnel", "treemap", "radar", "tornado"].includes(widget.type) && <SeriesColorsEditor opts={opts} patchOpts={patchOpts} />}
    </Section>
  );
}

// Section 6 — Data labels
export function InspectorLabelsSection({ ins }) {
  const { widget, opts, patchOpts } = ins;
  return (
    <Section value="labels" icon={Tags} title="Data Labels" testid="inspector-sec-labels"
      hint="Show, position and format the value labels on data points.">
      <StyleToggle label="Show data labels" testid="style-labels" checked={["pie", "donut", "funnel"].includes(widget.type) ? opts.showLabels !== false : !!opts.showLabels} onChange={(v) => patchOpts({ showLabels: v })} />
      {CARTESIAN_TYPES.includes(widget.type) && <DataLabelEditor opts={opts} patchOpts={patchOpts} />}
      {widget.type === "combo" && <ComboLabelEditor opts={opts} patchOpts={patchOpts} />}
      {["pie", "donut"].includes(widget.type) && <PieStyleEditor opts={opts} patchOpts={patchOpts} />}
      {widget.type === "funnel" && <FunnelStyleEditor opts={opts} patchOpts={patchOpts} />}
    </Section>
  );
}

// Section 7 — Legend
export function InspectorLegendSection({ ins }) {
  const { opts, patchOpts } = ins;
  return (
    <Section value="legend" icon={ListOrdered} title="Legend" testid="inspector-sec-legend"
      hint="Legend visibility, position, size and per-series renaming.">
      <div className="flex items-center justify-between gap-2 py-1">
        <Label className="text-sm text-[color:var(--ink-2)]">Legend</Label>
        <Select value={opts.legendMode || (opts.showLegend === false ? "hide" : "auto")} onValueChange={(v) => patchOpts({ legendMode: v, showLegend: v !== "hide" })}>
          <SelectTrigger data-testid="style-legend" className="bg-[color:var(--surface)] w-[110px] h-8 text-xs shrink-0"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="auto">Auto</SelectItem>
            <SelectItem value="show">Always</SelectItem>
            <SelectItem value="hide">Hidden</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <LegendEditor opts={opts} patchOpts={patchOpts} />
    </Section>
  );
}

// Section 8 — Axes & Gridlines
export function InspectorAxesSection({ ins }) {
  const { widget, opts, patchOpts, hasAxisEditor, isGeo } = ins;
  return (
    <Section value="axes" icon={Ruler} title="Axes & Gridlines" testid="inspector-sec-axes"
      hint="Axis titles, bounds, rotation, units and grid lines.">
      {!isGeo && !hasAxisEditor && <StyleToggle label="Show grid lines" testid="style-grid" checked={opts.showGrid !== false} onChange={(v) => patchOpts({ showGrid: v })} />}
      {hasAxisEditor && <AxisEditor opts={opts} patchOpts={patchOpts} type={widget.type} />}
      {(hasAxisEditor || widget.type === "heatmap") && <AxisFmtEditor opts={opts} patchOpts={patchOpts} type={widget.type} />}
    </Section>
  );
}

// Section 9 — Tooltip (rich content + visual styling, items 55/181)
export function InspectorTooltipSection({ ins }) {
  const { widget, config, opts, patchOpts, patchConfig, measureFields, disp } = ins;
  return (
    <Section value="tooltip" icon={MessageSquare} title="Tooltip" testid="inspector-sec-tooltip"
      hint="What the hover tooltip shows and how it looks.">
      {widget.dataset_id && <TooltipEditor config={config} opts={opts} patchOpts={patchOpts} patchConfig={patchConfig} fields={measureFields} disp={disp} alwaysExtras={widget.type === "point_map"} />}
      <TooltipStyleEditor opts={opts} patchOpts={patchOpts} />
    </Section>
  );
}

// Section 10 — Chart-specific visual options
export function InspectorChartStyleSection({ ins }) {
  const {
    widget, config, opts, patchOpts, patchConfig, showStyle, isGeo, is3d, isImage,
    isPointMap, isGoogleMap, isDateDim, dims, datasets,
  } = ins;
  return (
    <Section value="chartstyle" icon={Shapes} title="Visual Options" testid="inspector-sec-chartstyle"
      hint="Options specific to this visual type (bars, lines, slices, maps…).">
      {widget.type === "kpi" && <KpiEditor config={config} opts={opts} patchOpts={patchOpts} fields={ins.numericFields || []} />}
      {widget.type === "table" && <TableEditor config={config} opts={opts} patchOpts={patchOpts} />}
      {widget.type === "pivot" && <PivotEditor opts={opts} patchOpts={patchOpts} config={config} />}
      {widget.type === "image" && <ImageWidgetEditor config={config} patchConfig={patchConfig} datasets={datasets || []} />}
      {showStyle && (
        <>
          {["bar", "bar_stacked", "line", "area", "combo", "scatter", "pie", "donut", "funnel", "tornado"].includes(widget.type) && (
            <SortEditor config={config} opts={opts} patchOpts={patchOpts} isDateDim={isDateDim} />
          )}
          {(widget.type === "line" || widget.type === "area") && (
            <StyleToggle label="Smooth lines" testid="style-smooth" checked={opts.smooth !== false} onChange={(v) => patchOpts({ smooth: v })} />
          )}
          {widget.type === "donut" && (
            <div>
              <Label className="text-xs text-[color:var(--ink-3)]">Inner radius ({opts.donutRadius || 45}%)</Label>
              <Slider className="mt-2" min={20} max={75} step={5} value={[opts.donutRadius || 45]} onValueChange={(v) => patchOpts({ donutRadius: v[0] })} />
            </div>
          )}
          {["pie", "donut"].includes(widget.type) && (
            <StyleToggle label="Semi donut (half circle)" testid="style-semidonut" checked={!!opts.semiDonut} onChange={(v) => patchOpts({ semiDonut: v })} />
          )}
          {widget.type === "combo" && <ComboSeriesEditor config={config} opts={opts} patchOpts={patchOpts} />}
          {widget.type === "tornado" && <TornadoStyleEditor opts={opts} patchOpts={patchOpts} />}
          {["bar", "bar_stacked", "line", "area", "combo", "scatter"].includes(widget.type) && <AnalyticsEditor opts={opts} patchOpts={patchOpts} />}
          {is3d && <StyleToggle label="Auto-rotate" testid="style-autorotate" checked={!!opts.autoRotate} onChange={(v) => patchOpts({ autoRotate: v })} />}
          {isImage && (
            <div className="space-y-3 rounded-xl border border-[color:var(--glass-border)] bg-[color:var(--surface-2)] p-3">
              <Label className="text-xs text-[color:var(--ink-2)] flex items-center gap-1.5"><ImageIcon className="w-3.5 h-3.5" />Image markers</Label>
              <div>
                <Label className="text-[11px] text-[color:var(--ink-3)]">Image URL (used as the point / bar icon)</Label>
                <div className="mt-1.5"><MediaInput testid="style-image-url" value={opts.imageUrl || ""} onChange={(v) => patchOpts({ imageUrl: v })} placeholder="https://.../icon.png" /></div>
              </div>
              <div>
                <Label className="text-[11px] text-[color:var(--ink-3)]">Marker size ({opts.imageSize || (widget.type === "pictorial_bar" ? 22 : 30)}px)</Label>
                <Slider className="mt-2" min={12} max={64} step={2} value={[opts.imageSize || (widget.type === "pictorial_bar" ? 22 : 30)]} onValueChange={(v) => patchOpts({ imageSize: v[0] })} />
              </div>
              <p className="text-[11px] text-[color:var(--ink-3)]">Tip: use a transparent PNG / SVG for best results. Leave empty to use a default shape.</p>
            </div>
          )}
          {!isGeo && !is3d && (
            <div className="space-y-3 rounded-xl border border-[color:var(--glass-border)] bg-[color:var(--surface-2)] p-3">
              <Label className="text-xs text-[color:var(--ink-2)] flex items-center gap-1.5"><ImageIcon className="w-3.5 h-3.5" />Background image</Label>
              <div>
                <Label className="text-[11px] text-[color:var(--ink-3)]">Watermark URL (behind the chart)</Label>
                <div className="mt-1.5"><MediaInput testid="style-bgimage-url" value={opts.bgImageUrl || ""} onChange={(v) => patchOpts({ bgImageUrl: v })} placeholder="https://.../logo.png" /></div>
              </div>
              {opts.bgImageUrl && (
                <div>
                  <Label className="text-[11px] text-[color:var(--ink-3)]">Opacity ({Math.round((opts.bgImageOpacity ?? 0.08) * 100)}%)</Label>
                  <Slider className="mt-2" min={2} max={40} step={2} value={[Math.round((opts.bgImageOpacity ?? 0.08) * 100)]} onValueChange={(v) => patchOpts({ bgImageOpacity: v[0] / 100 })} />
                </div>
              )}
            </div>
          )}
          {BAR_TYPES.includes(widget.type) && <BarStyleEditor opts={opts} patchOpts={patchOpts} type={widget.type} />}
          {["line", "area", "combo"].includes(widget.type) && <LineStyleEditor opts={opts} patchOpts={patchOpts} type={widget.type} />}
          {["line", "area", "combo"].includes(widget.type) && <SeriesStyleEditor config={config} opts={opts} patchOpts={patchOpts} type={widget.type} />}
          {widget.type === "scatter" && <ScatterStyleEditor opts={opts} patchOpts={patchOpts} />}
          {widget.type === "radar" && <RadarStyleEditor opts={opts} patchOpts={patchOpts} config={config} />}
          {widget.type === "gauge" && <GaugeStyleEditor opts={opts} patchOpts={patchOpts} />}
          {widget.type === "treemap" && <TreemapStyleEditor opts={opts} patchOpts={patchOpts} />}
          {widget.type === "heatmap" && <HeatmapStyleEditor opts={opts} patchOpts={patchOpts} />}
          {isGeo && <MapStyleEditor opts={opts} patchOpts={patchOpts} widget={widget} config={config} patchConfig={patchConfig} numericFields={ins.numericFields || []} />}
          {isPointMap && <PointMapStyleEditor config={config} patchConfig={patchConfig} dims={dims} />}
          {isGoogleMap && <GoogleMapStyleEditor config={config} patchConfig={patchConfig} />}
        </>
      )}
    </Section>
  );
}

// Section 11 — Number Format
export function InspectorNumberFormatSection({ ins }) {
  const { widget, config, opts, patchConfig, patchOpts } = ins;
  return (
    <Section value="numfmt" icon={Hash} title="Number Format" testid="inspector-sec-numfmt"
      hint="Decimals, prefixes/suffixes, units and empty-value display per measure.">
      <MeasureFormatEditor config={config} patchConfig={patchConfig} />
      {["kpi", "table", "pivot"].includes(widget.type) && <EmptyAsSelect opts={opts} patchOpts={patchOpts} />}
      <div className="flex items-center justify-between gap-2 pt-1">
        <Label className="text-[11px] text-[color:var(--ink-3)] shrink-0">Locale override (this widget)</Label>
        <Select value={opts.locale || "__auto__"} onValueChange={(v) => patchOpts({ locale: v === "__auto__" ? "" : v })}>
          <SelectTrigger data-testid="widget-locale-override" className="bg-[color:var(--surface)] w-[150px] h-7 text-xs"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="__auto__">Dashboard default</SelectItem>
            {WIDGET_LOCALES.map((l) => <SelectItem key={l.v} value={l.v}>{l.label}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
    </Section>
  );
}

// Item 20: per-widget regional number formatting override
const WIDGET_LOCALES = [
  { v: "en-US", label: "English (US) 1,234.56" },
  { v: "en-GB", label: "English (UK)" },
  { v: "en-IN", label: "English (India) 1,23,456" },
  { v: "ar-AE", label: "العربية (UAE)" },
  { v: "ar-SA", label: "العربية (Saudi)" },
  { v: "fr-FR", label: "Français 1 234,56" },
  { v: "de-DE", label: "Deutsch 1.234,56" },
  { v: "sw-KE", label: "Kiswahili (Kenya)" },
  { v: "sw-TZ", label: "Kiswahili (Tanzania)" },
  { v: "id-ID", label: "Indonesia" },
  { v: "ms-MY", label: "Melayu (Malaysia)" },
  { v: "th-TH", label: "ไทย (Thailand)" },
  { v: "zh-CN", label: "中文 (China)" },
  { v: "ja-JP", label: "日本語 (Japan)" },
];

// Section 12 — Conditional Formatting (rules + value-range colors, item 180)
export function InspectorCondFormatSection({ ins }) {
  const { widget, config, opts, patchOpts, showCondFormat, showStyle } = ins;
  return (
    <Section value="condfmt" icon={Paintbrush} title="Conditional Formatting" testid="inspector-sec-condfmt"
      hint="Paint values by rules or numeric ranges.">
      {showCondFormat && <CondFormatEditor config={config} opts={opts} patchOpts={patchOpts} widgetType={widget.type} />}
      {showStyle && ADV_COLOR_TYPES.includes(widget.type) && <AdvancedColorEditor opts={opts} patchOpts={patchOpts} />}
    </Section>
  );
}
